import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm, change } from 'redux-form';
import { Button, Col, Modal, ModalBody, ModalHeader, Row } from 'reactstrap';
import { connect } from 'react-redux';
import { GRL_URL } from '../../../../../../utils/pnd/PndConstants';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import PersonIcon from '@mui/icons-material/Person';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import PhoneIcon from '@mui/icons-material/Phone';
import BadgeIcon from '@mui/icons-material/Badge';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DomainIcon from '@mui/icons-material/Domain';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LayersIcon from '@mui/icons-material/Layers';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import SettingsIcon from '@mui/icons-material/Settings';
import ContactsIcon from '@mui/icons-material/Contacts';
import BusinessIcon from '@mui/icons-material/Business';

import renderToggleButtonField from 'src/shared/components/form/ToggleButton';
import renderSelectField from 'src/shared/components/form/Select';
import renderField from 'src/shared/components/form/renderField';
import { isNumber, mobileNoFormat, mobileNoLength } from 'src/utils/validationUtils';

const required = value => value ? undefined : 'Required'

// Local renderer for textarea to handle multi-line address without touching shared renderField
const renderTextareaField = ({ input, placeholder, meta: { touched, error, warning }, icon: Icon, disabled, rows = 2 }) => (
    <div className="form__form-group mb-0">
        <div className="form__form-group-field">
            <div className={`input-group w-100 ${touched && error ? 'has-validation' : ''}`} style={{ flexWrap: 'nowrap' }}>
                {Icon && (
                    <span className="input-group-text bg-light border-end-0 text-muted d-flex align-items-start pt-2" style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}>
                        <Icon style={{ fontSize: '1.2rem' }} />
                    </span>
                )}
                <textarea
                    {...input}
                    placeholder={placeholder}
                    disabled={disabled}
                    rows={rows}
                    className={`form-control form-control-sm ${touched && error ? 'is-invalid' : ''} ${Icon ? 'border-start-0' : ''}`}
                    style={{
                        fontSize: '0.9rem',
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                        minHeight: '60px',
                        resize: 'none'
                    }}
                />
                {touched && error && <div className="invalid-feedback d-block w-100">{error}</div>}
                {touched && warning && <div className="text-warning small w-100">{warning}</div>}
            </div>
        </div>
    </div>
);

const CustomerAddOrUpdate = ({ handleSubmit, setMapData, address, status, dispatch, setMapInfo, dataInfo }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isAddress, setAddress] = useState(null);
    const [apaIframeUrl] = useState(GRL_URL);
    const apaFrameRef = useRef(null);

    const toggleModal = () => {
        setIsModalOpen(!isModalOpen);
    };

    const handleFindLocation = () => {
        toggleModal();
        handleMessageToIframe();
    };

    const handleMessageToIframe = () => {
        const datajson = '{"RequestID": ' + Math.floor((Math.random() * 20) + 1) + ',"MessageType":1,"AddressElements": [{"AddressElementTypeCode": 1,"Value":""},{"AddressElementTypeCode":2,"Value": ""},{ "AddressElementTypeCode": 3,"Value": ""},{"AddressElementTypeCode":4,"Value": ""},{"AddressElementTypeCode": 5,"Value":"520950"},{"AddressElementTypeCode": 6,"Value":"" },{"AddressElementTypeCode": 7, "Value": "" },{"AddressElementTypeCode": 8,"Value":""}]}';
        if (apaFrameRef.current) {
            apaFrameRef.current.contentWindow.postMessage(datajson, '*');
            window.addEventListener('message', handleMessageFromIframe);
        }
    };

    let deliveryAddress = "";
    const handleMessageFromIframe = (event) => {
        if (event && event.data) {
            try {
                const jsonRegex = /^\s*(\{.*\}|\[.*\])\s*$/;
                if (jsonRegex.test(event?.data)) {
                    const data_from_apa = JSON.parse(event.data);
                    setMapInfo(data_from_apa);
                    const buildingName = data_from_apa.AddressElements.find(element => element.AddressElementTypeCode === 6);
                    const landmark = data_from_apa.AddressElements.find(element => element.AddressElementTypeCode === 4);
                    const street = data_from_apa.AddressElements.find(element => element.AddressElementTypeCode === 3);
                    const area = data_from_apa.AddressElements.find(element => element.AddressElementTypeCode === 2);
                    const zone = data_from_apa.AddressElements.find(element => element.AddressElementTypeCode === 7);
                    const city = data_from_apa.AddressElements.find(element => element.AddressElementTypeCode === 8);
                    const emirates = data_from_apa.AddressElements.find(element => element.AddressElementTypeCode === 1);

                    if (buildingName && buildingName?.Value !== "") {
                        deliveryAddress = deliveryAddress + buildingName.Value + ",";
                        dispatch(change('CustomerAddOrUpdateForm', 'buildingName', buildingName.Value));
                    }
                    if (buildingName?.Value === "" && landmark?.Value !== "") {
                        deliveryAddress = deliveryAddress + landmark.Value + ",";
                    }
                    if (street) deliveryAddress = deliveryAddress + street.Value + ",";
                    if (area) deliveryAddress = deliveryAddress + area.Value + ",";
                    if (zone) deliveryAddress = deliveryAddress + zone.Value + ",";
                    if (city) deliveryAddress = deliveryAddress + city.Value + ",";
                    if (emirates) deliveryAddress = deliveryAddress + emirates.Value;
                    let finalAddress = deliveryAddress;
                    if (finalAddress.endsWith(',')) {
                        finalAddress = finalAddress.slice(0, -1);
                    }
                    finalAddress = finalAddress.replace(/,,/g, ',');

                    setAddress(finalAddress);
                    setMapData(finalAddress);
                    dispatch(change('CustomerAddOrUpdateForm', 'address', finalAddress));
                    
                    toggleModal();
                }
            } catch (error) {
                console.error('event.data is not a valid JSON string');
            }
        }
    };

    return (
        <form className="form p-4" onSubmit={handleSubmit} style={{ backgroundColor: '#fff' }}>
            {/* --- Section: Customer Info --- */}
            <div className="mb-4">
                <div className="d-flex align-items-center mb-3 border-bottom pb-2">
                    <BusinessIcon className="text-primary me-2" style={{ fontSize: '1.4rem' }} />
                    <h5 className="m-0 fw-bold text-dark text-uppercase" style={{ fontSize: '1rem', letterSpacing: '0.5px' }}>Identity & Primary Contact</h5>
                </div>
                <Row className="g-4">
                    <Col lg={12}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-1">Customer Name <span className='text-danger'>*</span></label>
                            <Field name="customerName" component={renderField} type="text" placeholder="Full Legal Name" validate={[required]} icon={PersonIcon} />
                        </div>
                    </Col>
                    <Col lg={6}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-1">Contact Person</label>
                            <Field name="contactName" component={renderField} type="text" placeholder="Name of representative" icon={PersonIcon} />
                        </div>
                    </Col>
                    {dataInfo && (
                        <Col lg={6}>
                            <div className="mb-1">
                                <label className="form-label fw-bold text-muted small mb-1">Customer Identification Code</label>
                                <Field name="merchantCustomerCode" component={renderField} type="text" placeholder="Ref Code" disabled={true} icon={BadgeIcon} />
                            </div>
                        </Col>
                    )}
                </Row>
            </div>

            {/* --- Section: Communication --- */}
            <div className="mb-4">
                <div className="d-flex align-items-center mb-3 border-bottom pb-2">
                    <ContactsIcon className="text-primary me-2" style={{ fontSize: '1.4rem' }} />
                    <h5 className="m-0 fw-bold text-dark text-uppercase" style={{ fontSize: '1rem', letterSpacing: '0.5px' }}>Communication Details</h5>
                </div>
                <Row className="g-4">
                    <Col lg={6}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-1">Mobile Number <span className='text-danger'>*</span></label>
                            <Field name="mobileNo" component={renderField} type="text" placeholder="971xxxxxxx" validate={[required, mobileNoFormat, mobileNoLength]} icon={SmartphoneIcon} />
                        </div>
                    </Col>
                    <Col lg={6}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-1">Telephone Line</label>
                            <Field name="telephoneNo" component={renderField} type="text" placeholder="Landline connection" validate={isNumber} icon={PhoneIcon} />
                        </div>
                    </Col>
                </Row>
            </div>

            {/* --- Section: Physical Address --- */}
            <div className="mb-4">
                <div className="d-flex align-items-center mb-3 border-bottom pb-2">
                    <LocationOnIcon className="text-primary me-2" style={{ fontSize: '1.4rem' }} />
                    <h5 className="m-0 fw-bold text-dark text-uppercase" style={{ fontSize: '1rem', letterSpacing: '0.5px' }}> Address</h5>
                </div>
                <Row className="g-4">
                    <Col lg={12}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-1">Full Address Description <span className='text-danger'>*</span></label>
                            <div className="d-flex align-items-start">
                                <div className="flex-grow-1">
                                    <Field name="address" component={renderTextareaField} rows={2} placeholder="Enter full address details or use map" validate={[required]} icon={LocationOnIcon} />
                                </div>
                                <Button color="success" size="sm" className="ms-2 d-flex align-items-center justify-content-center shadow-sm" onClick={handleFindLocation} style={{ minWidth: '42px', width: '42px', height: '42px', marginTop: '1px', borderRadius: '8px' }} title="Pin Location on Map">
                                    <AddLocationAltIcon style={{ fontSize: '1.4rem' }} />
                                </Button>
                            </div>
                        </div>
                    </Col>
                    <Col lg={4}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-1">Label Address As <span className='text-danger'>*</span></label>
                            <Field validate={[required]} name="addressBookName" component={renderSelectField} options={[
                                { value: 1, label: "Home" },
                                { value: 2, label: "Work" },
                                { value: 3, label: "Other" },
                            ]} placeholder="Address Title" />
                        </div>
                    </Col>
                    <Col lg={8}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-1">Building/Villa Name <span className='text-danger'>*</span></label>
                            <Field name="buildingName" component={renderField} type="text" placeholder="Name of house or complex" validate={[required]} icon={DomainIcon} />
                        </div>
                    </Col>
                    <Col lg={12}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-1">P.O. Box Number</label>
                            <Field name="poBoxNo" component={renderField} type="text" placeholder="Post office box (if available)" validate={[isNumber]} icon={MailOutlineIcon} />
                        </div>
                    </Col>
                </Row>
            </div>

            {/* --- Section: Unit Details --- */}
            <div className="mb-4">
                <div className="d-flex align-items-center mb-3 border-bottom pb-2">
                    <LayersIcon className="text-primary me-2" style={{ fontSize: '1.4rem' }} />
                    <h5 className="m-0 fw-bold text-dark text-uppercase" style={{ fontSize: '1rem', letterSpacing: '0.5px' }}>Unit & Residency Details</h5>
                </div>
                <Row className="g-4">
                    <Col lg={4}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-1">Floor Level <span className='text-danger'>*</span></label>
                            <Field name="floorNo" component={renderField} type="text" placeholder="Level/Floor" validate={[required]} icon={LayersIcon} />
                        </div>
                    </Col>
                    <Col lg={4}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-1">Door/Flat Number <span className='text-danger'>*</span></label>
                            <Field name="doorNo" component={renderField} type="text" placeholder="Apartment or Villa No." validate={[required]} icon={MeetingRoomIcon} />
                        </div>
                    </Col>
                    <Col lg={4}>
                        <div className="d-flex align-items-center justify-content-between px-2 mb-3">
                            <span className="fw-bold text-muted small">Status:</span>
                            <Field name="status" component={renderToggleButtonField} defaultChecked={status} />
                        </div>
                    </Col>
                </Row>
            </div>


            {/* --- Form Actions --- */}
            <div className="d-flex justify-content-center pb-2">
                <Button color="success" size="sm" type="submit" className="fw-bold p-0 shadow-sm" style={{ fontSize: '0.75rem', width: '85px !important', height: '30px !important', lineHeight: '30px', borderRadius: '4px' }}>
                    SAVE
                </Button>
            </div>

            <Modal isOpen={isModalOpen} toggle={toggleModal} style={{ maxWidth: '1100px' }} centered>
                <ModalHeader toggle={toggleModal} className="bg-primary text-white py-3">Point Address on Satellite Map</ModalHeader>
                <ModalBody className="p-0">
                    <iframe ref={apaFrameRef} src={apaIframeUrl} width="100%" height="700px" title="Get Address" onLoad={handleMessageToIframe} style={{ border: 'none' }}></iframe>
                </ModalBody>
            </Modal>
        </form>
    );
};

CustomerAddOrUpdate.propTypes = {
    handleSubmit: PropTypes.func.isRequired,
};

const getAddressBookNameInitialValue = (dataInfo) => {
    if (dataInfo) {
        switch (dataInfo) {
            case "Home": return { value: 1, label: "Home" };
            case "Work": return { value: 2, label: "Work" };
            case "Other": return { value: 3, label: "Other" };
            default: return null;
        }
    }
    return null;
};

const mapStateToProps = (state, data) => {
    if (data && data?.dataInfo) {
        return {
            initialValues: {
                merchantCustomerId: data?.dataInfo.merchantCustomerId,
                customerName: data?.dataInfo.customerName,
                merchantCustomerCode: data?.dataInfo.merchantCustomerCode,
                buildingName: data?.dataInfo.buildingName,
                telephoneNo: data?.dataInfo.telephoneNo,
                customerContactName: data?.dataInfo.customerContactName,
                mobileNo: data?.dataInfo.mobileNo,
                latitude: data.dataInfo.latitude,
                poBoxNo: data.dataInfo.poBoxNo,
                contactName: data.dataInfo.contactName,
                doorNo: data.dataInfo.doorNo,
                floorNo: data.dataInfo.floorNo,
                addressBookName: getAddressBookNameInitialValue(data.dataInfo.addressBookName),
                faxNo: data?.dataInfo.faxNo,
                merchantCustomerAddBookId: data?.dataInfo?.merchantCustomerAddBookId,
            }
        }
    }
    return {};
}

export default connect(mapStateToProps)(reduxForm({ form: 'CustomerAddOrUpdateForm', enableReinitialize: true })(CustomerAddOrUpdate));